import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
    CpmButton,
    CpmFilterDropdown,
    CpmModal,
    CpmModalBody,
    CpmModalFooter,
    CpmStatusChip,
    CpmTypeChip,
} from '../../../../components/design-system';
import {
    CPM_CONTAINER_TYPE_OPTIONS,
    CPM_LICENSE_TYPES_COPY,
    CPM_LICENCE_EXCISE_COPY,
    CPM_VOLUME_DEFAULT_UNIT,
    cpmLicenceExcisePickForState,
    cpmLicenseTypeOptions,
    formatCpmCount,
    formatVolumeLabel,
    isActiveExciseCategory,
    volumeUnitShortLabel,
} from '../../../../constants/centralProductMaster.constants';
import {
    createMaster,
    createVolumeSetting,
    getMasterDetails,
    listExciseCategories,
    listMasters,
    listStates,
    listVolumeSettings,
    updateMaster,
} from '../../../../services/productMasterService';
import type {
    CpmExciseCategory,
    CpmLicenseTypesByState,
    CpmMasterDetailsResponse,
    CpmProductRecord,
    CpmVolumeSetting,
} from '../../../../types/centralProductMaster.types';
import { getApiErrorMessage } from '../../../../utils/api/ApiClient';
import { toast } from '../../../../utils/toast';

type FormLevel = 'brand' | 'product' | 'variant' | 'stateProduct';

interface RecordFormDialogProps {
    open: boolean;
    level: FormLevel;
    id: string | null;
    /** Prefill product when creating a variant from a product row. */
    createProductId?: string | null;
    createProductName?: string | null;
    onClose: () => void;
    onSaved: () => void;
    onRequestDelete?: (level: FormLevel, id: string, name: string) => void;
    /** Push a variant edit on top of this product dialog (keeps product mounted). */
    onOpenVariant?: (variantId: string) => void;
    /** Stacking: higher than the layer underneath. Defaults to 60. */
    zIndex?: number;
    /**
     * Stacking: only the top dialog should dismiss on Escape / backdrop.
     * Underlying layers keep their card visible but do not capture dismiss.
     */
    isTop?: boolean;
    /** Per-state curated license types. If omitted, the dialog loads listStates once. */
    licenseTypesByState?: CpmLicenseTypesByState;
    licenseTypesReady?: boolean;
}

function ChipListEditor({
    values,
    onChange,
    placeholder,
    addLabel,
    emptyLabel = 'None yet',
    changed = false,
}: {
    values: string[];
    onChange: (next: string[]) => void;
    placeholder: string;
    addLabel: string;
    emptyLabel?: string;
    changed?: boolean;
}) {
    const [adding, setAdding] = useState(false);
    const [text, setText] = useState('');

    const commit = () => {
        const v = text.trim();
        if (v && !values.includes(v)) onChange([...values, v]);
        setText('');
        setAdding(false);
    };

    return (
        <div
            className="flex flex-wrap items-center rounded-[10px] border-[1.5px] transition-colors"
            style={{
                gap: 6,
                padding: '6px 8px',
                minHeight: 42,
                borderColor: changed ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)',
                background: changed ? 'rgba(110,100,240,.06)' : '#fff',
            }}
        >
            {values.map((v) => (
                <span
                    key={v}
                    className="inline-flex items-center gap-1 px-[9px] py-[3px] rounded-full bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-strong)] text-[12px] font-extrabold text-[var(--cpm-text-secondary)]"
                >
                    <span className="max-w-[200px] truncate">{v}</span>
                    <button
                        type="button"
                        className="inline-flex items-center justify-center w-[17px] h-[17px] rounded-full bg-[var(--cpm-surface-skeleton)] text-[var(--cpm-text-secondary)]"
                        onClick={() => onChange(values.filter((x) => x !== v))}
                    >
                        <svg
                            width="9"
                            height="9"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3.4"
                        >
                            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                        </svg>
                    </button>
                </span>
            ))}
            {values.length === 0 && !adding ? (
                <span className="text-[12px] font-bold text-[var(--cpm-text-tertiary)] pl-[3px]">
                    {emptyLabel}
                </span>
            ) : null}
            {adding ? (
                <input
                    autoFocus
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            commit();
                        }
                        if (e.key === 'Escape') {
                            setAdding(false);
                            setText('');
                        }
                    }}
                    onBlur={commit}
                    placeholder={placeholder}
                    className="flex-1 min-w-[140px] h-[28px] px-[9px] rounded-[8px] border-[1.5px] border-[var(--cpm-brand-purple)] bg-white text-[12px] font-bold text-[var(--cpm-text-primary)] outline-none"
                />
            ) : (
                <button
                    type="button"
                    onClick={() => setAdding(true)}
                    className="inline-flex items-center gap-1 px-[10px] py-[4px] rounded-full border-[1.5px] border-dashed border-[var(--cpm-border-strong)] bg-white text-[11.5px] font-extrabold text-[var(--cpm-text-secondary)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
                >
                    <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                    >
                        <path d="M5 12h14M12 5v14" strokeLinecap="round" />
                    </svg>
                    {addLabel}
                </button>
            )}
        </div>
    );
}

function FieldLabel({
    label,
    tag,
    tagChanged = false,
}: {
    label: string;
    tag?: string;
    tagChanged?: boolean;
}) {
    return (
        <div className="flex items-center gap-2">
            <span
                className="uppercase text-[var(--cpm-text-secondary)]"
                style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em' }}
            >
                {label}
            </span>
            {tag ? (
                <span
                    className={
                        tagChanged
                            ? 'inline-flex items-center px-2 py-[2px] rounded-full bg-[var(--cpm-purple-tint-13)] text-[9.5px] font-extrabold tracking-[0.05em] uppercase text-[var(--cpm-brand-purple)]'
                            : 'inline-flex items-center px-2 py-[2px] rounded-full bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)] text-[9.5px] font-extrabold tracking-[0.05em] uppercase text-[var(--cpm-text-tertiary)]'
                    }
                >
                    {tag}
                </span>
            ) : null}
        </div>
    );
}

function FormField({
    label,
    tag,
    tagChanged,
    span2,
    help,
    children,
}: {
    label: string;
    tag?: string;
    tagChanged?: boolean;
    span2?: boolean;
    help?: string;
    children: ReactNode;
}) {
    return (
        <div className={`flex flex-col gap-[6px]${span2 ? ' col-span-2' : ''}`}>
            <FieldLabel label={label} tag={tag} tagChanged={tagChanged} />
            {children}
            {help ? (
                <span className="text-[11.5px] font-semibold leading-[1.45] text-[var(--cpm-text-secondary)] text-pretty">
                    {help}
                </span>
            ) : null}
        </div>
    );
}

function statusDisplayLabel(status: string) {
    const map: Record<string, string> = {
        ACTIVE: 'Active',
        PENDING: 'Pending',
        NEEDS_REVIEW: 'Needs review',
        MERGED: 'Merged',
        REJECTED: 'Rejected',
        CONFLICTED: 'Conflicted',
    };
    return map[status] || status || '—';
}

function sameStringList(a: unknown, b: unknown) {
    const aa = Array.isArray(a) ? a.map(String) : [];
    const bb = Array.isArray(b) ? b.map(String) : [];
    return aa.length === bb.length && aa.every((v, i) => v === bb[i]);
}

function TextInput({
    value,
    onChange,
    placeholder,
    type = 'text',
    changed = false,
}: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    type?: string;
    changed?: boolean;
}) {
    return (
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full h-[42px] px-[13px] rounded-[10px] border-[1.5px] text-[13.5px] font-bold text-[var(--cpm-text-primary)] outline-none focus:border-[var(--cpm-brand-purple)] focus:shadow-[var(--cpm-ring-focus)]"
            style={{
                borderColor: changed ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)',
                background: changed ? 'rgba(110,100,240,.05)' : '#fff',
            }}
        />
    );
}

function ReadonlyField({ value, secondary }: { value: string; secondary?: string }) {
    return (
        <div
            className="flex items-center gap-[10px] px-[13px] rounded-[10px] bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)]"
            style={{ minHeight: 42 }}
        >
            <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 2 }}>
                <span className="text-[13.5px] font-bold text-[var(--cpm-text-primary)] truncate">
                    {value || '—'}
                </span>
                {secondary ? (
                    <span className="text-[11px] font-semibold text-[var(--cpm-text-tertiary)] tabular truncate">
                        {secondary}
                    </span>
                ) : null}
            </div>
            <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--cpm-text-tertiary)"
                strokeWidth="2"
                className="shrink-0"
            >
                <rect width="18" height="11" x="3" y="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
        </div>
    );
}

export default function RecordFormDialog({
    open,
    level,
    id,
    createProductId = null,
    createProductName = null,
    onClose,
    onSaved,
    onRequestDelete,
    onOpenVariant,
    zIndex = 60,
    isTop = true,
    licenseTypesByState,
    licenseTypesReady = false,
}: RecordFormDialogProps) {
    const isCreate = !id;
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loadMissing, setLoadMissing] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [loadKey, setLoadKey] = useState(0);
    const [details, setDetails] = useState<CpmMasterDetailsResponse | null>(null);
    const [draft, setDraft] = useState<Record<string, unknown>>({});
    const [volumes, setVolumes] = useState<CpmVolumeSetting[]>([]);
    const [products, setProducts] = useState<CpmProductRecord[]>([]);
    const [exciseCategories, setExciseCategories] = useState<CpmExciseCategory[]>([]);
    const [volAdding, setVolAdding] = useState(false);
    const [volText, setVolText] = useState('');
    const [volSaving, setVolSaving] = useState(false);
    const [volError, setVolError] = useState<string | null>(null);
    const [localLicenseMap, setLocalLicenseMap] = useState<CpmLicenseTypesByState | null>(null);
    const [localLicenseReady, setLocalLicenseReady] = useState(false);

    const setField = (key: string, value: unknown) =>
        setDraft((prev) => ({ ...prev, [key]: value }));

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        setLoadMissing(false);
        setSaveError(null);
        setDraft({});
        setDetails(null);
        setExciseCategories([]);
        setVolAdding(false);
        setVolText('');
        setVolSaving(false);
        setVolError(null);

        const load = async () => {
            try {
                if (isCreate && level === 'brand') {
                    if (cancelled) return;
                    setDraft({
                        name: '',
                        aliases: [],
                        countryOfOrigin: '',
                        parentCompany: '',
                    });
                } else if (isCreate && level === 'variant') {
                    const [volData, prodData] = await Promise.all([
                        listVolumeSettings({ status: 'ACTIVE' }),
                        listMasters({ level: 'product', status: 'ACTIVE', page: 1, limit: 50 }),
                    ]);
                    if (cancelled) return;
                    setVolumes(volData.settings ?? []);
                    setProducts((prodData.items as CpmProductRecord[]) ?? []);
                    setDraft({
                        alcoholProduct: createProductId ?? '',
                        containerType: 'Glass Bottle',
                        itemPerCase: '',
                        aliases: [],
                        gtin: [],
                    });
                } else if (id) {
                    const data = await getMasterDetails({ level, id });
                    if (cancelled) return;
                    if (!data?.record) {
                        setLoadMissing(true);
                        setLoadError("That record isn't in the master catalog");
                        return;
                    }
                    setDetails(data);
                    const rec = data.record ?? {};
                    if (level === 'brand') {
                        setDraft({
                            name: rec.name ?? '',
                            aliases: rec.aliases ?? [],
                            countryOfOrigin: rec.countryOfOrigin ?? '',
                            parentCompany: rec.parentCompany ?? '',
                        });
                    } else if (level === 'product') {
                        setDraft({
                            name: rec.name ?? '',
                            aliases: rec.aliases ?? [],
                            alcoholContent: rec.alcoholContent ?? '',
                            description: rec.description ?? '',
                        });
                    } else if (level === 'variant') {
                        setDraft({
                            name: rec.name ?? '',
                            aliases: rec.aliases ?? [],
                            containerType: rec.containerType ?? 'Glass Bottle',
                            itemPerCase: rec.itemPerCase ?? '',
                            gtin: rec.gtin ?? [],
                        });
                    } else {
                        const stateName = String(rec.state ?? '');
                        if (stateName) {
                            try {
                                const exciseData = await listExciseCategories({
                                    state: stateName,
                                    page: 1,
                                    limit: 200,
                                });
                                if (!cancelled) {
                                    setExciseCategories(
                                        (exciseData.categories ?? []).filter((c) =>
                                            isActiveExciseCategory(c)
                                        )
                                    );
                                }
                            } catch {
                                // Category picker stays empty; subcategory remains unsettable.
                            }
                        }
                        setDraft({
                            retailPrice: rec.retailPrice ?? '',
                            purchasePrice: rec.purchasePrice ?? '',
                            wholesalePrice: rec.wholesalePrice ?? '',
                            distilleryPrice: rec.distilleryPrice ?? '',
                            itemPerCase: rec.itemPerCase ?? '',
                            licenseType: rec.licenseType ?? [],
                            itemCode: rec.itemCode ?? [],
                            gtin: rec.gtin ?? [],
                            exciseCategory:
                                rec.exciseCategory != null ? String(rec.exciseCategory) : '',
                            // Write contract: ObjectId (not denormalized name).
                            exciseSubCategory:
                                rec.exciseSubCategory != null ? String(rec.exciseSubCategory) : '',
                        });
                    }
                }
            } catch (err) {
                if (cancelled) return;
                const msg = getApiErrorMessage(err);
                const missing =
                    /not found|404|isn't in the master|does not exist/i.test(msg) ||
                    msg.toLowerCase().includes('not found');
                setLoadMissing(missing);
                setLoadError(
                    missing
                        ? "That record isn't in the master catalog"
                        : msg || "Couldn't open this record"
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [open, level, id, isCreate, createProductId, loadKey]);

    useEffect(() => {
        if (!open || level !== 'stateProduct') return;
        if (licenseTypesByState != null) return;
        let cancelled = false;
        setLocalLicenseReady(false);
        void listStates()
            .then((states) => {
                if (cancelled) return;
                const map: CpmLicenseTypesByState = {};
                for (const s of states) {
                    map[s.name] = s.licenseTypes ?? [];
                }
                setLocalLicenseMap(map);
                setLocalLicenseReady(true);
            })
            .catch(() => {
                if (cancelled) return;
                setLocalLicenseMap({});
                setLocalLicenseReady(true);
            });
        return () => {
            cancelled = true;
        };
    }, [open, level, licenseTypesByState]);

    const resolvedLicenseMap = licenseTypesByState ?? localLicenseMap ?? {};
    const resolvedLicenseReady =
        licenseTypesByState != null ? licenseTypesReady : localLicenseReady;

    const recordName = String(
        isCreate
            ? draft.name || (level === 'brand' ? 'New brand' : 'New variant')
            : (details?.record?.name ?? 'Record')
    );
    const status = String(details?.record?.status ?? (isCreate ? 'ACTIVE' : ''));
    const tenantNote =
        level === 'product' && details?.tenantLinkCount != null
            ? `${formatCpmCount(details.tenantLinkCount)} store-brand ${
                  details.tenantLinkCount === 1 ? 'product' : 'products'
              } linked`
            : level === 'variant' && details?.tenantLinkCount != null
              ? `${formatCpmCount(details.tenantLinkCount)} tenant ${
                    details.tenantLinkCount === 1 ? 'link' : 'links'
                }`
              : null;

    const kindLabel =
        level === 'brand'
            ? 'Brand'
            : level === 'product'
              ? 'Product'
              : level === 'variant'
                ? 'Variant'
                : 'State product';

    const brandDirtyKeys = useMemo(() => {
        if (level !== 'brand' || !details?.record) return [] as string[];
        const rec = details.record;
        const keys: string[] = [];
        if (String(draft.name ?? '').trim() !== String(rec.name ?? '').trim()) keys.push('name');
        if (!sameStringList(draft.aliases, rec.aliases)) keys.push('aliases');
        if (String(draft.countryOfOrigin ?? '') !== String(rec.countryOfOrigin ?? '')) {
            keys.push('countryOfOrigin');
        }
        if (String(draft.parentCompany ?? '') !== String(rec.parentCompany ?? '')) {
            keys.push('parentCompany');
        }
        return keys;
    }, [level, details, draft]);

    const brandDirtySet = useMemo(() => new Set(brandDirtyKeys), [brandDirtyKeys]);

    const productDirtyKeys = useMemo(() => {
        if (level !== 'product' || !details?.record) return [] as string[];
        const rec = details.record;
        const keys: string[] = [];
        if (String(draft.name ?? '').trim() !== String(rec.name ?? '').trim()) keys.push('name');
        if (!sameStringList(draft.aliases, rec.aliases)) keys.push('aliases');
        const draftAbv =
            draft.alcoholContent === '' || draft.alcoholContent == null
                ? null
                : Number(draft.alcoholContent);
        const recAbv =
            rec.alcoholContent === '' || rec.alcoholContent == null
                ? null
                : Number(rec.alcoholContent);
        if (draftAbv !== recAbv) keys.push('alcoholContent');
        if (String(draft.description ?? '') !== String(rec.description ?? '')) {
            keys.push('description');
        }
        return keys;
    }, [level, details, draft]);

    const productDirtySet = useMemo(() => new Set(productDirtyKeys), [productDirtyKeys]);

    const canSave = (() => {
        if (loading) return false;
        if (isCreate && level === 'brand') {
            return String(draft.name || '').trim().length > 0;
        }
        if (isCreate && level === 'variant') {
            const name = String(draft.name || '').trim();
            const alcoholProduct = String(draft.alcoholProduct || '');
            const containerType = String(draft.containerType || '');
            const itemPerCase = Number(draft.itemPerCase);
            const volumeSettingId = String(draft.volumeSettingId || '');
            return (
                !!name &&
                !!alcoholProduct &&
                !!containerType &&
                itemPerCase > 0 &&
                !!volumeSettingId
            );
        }
        if (level === 'brand') {
            return String(draft.name || '').trim().length > 0 && brandDirtyKeys.length > 0;
        }
        if (level === 'product') {
            return (
                String(draft.name || '').trim().length > 0 &&
                draft.alcoholContent !== '' &&
                draft.alcoholContent != null &&
                !Number.isNaN(Number(draft.alcoholContent)) &&
                productDirtyKeys.length > 0
            );
        }
        if (level === 'variant') {
            return (
                String(draft.name || '').trim().length > 0 &&
                !!draft.containerType &&
                Number(draft.itemPerCase) > 0
            );
        }
        // State product — at least one editable field may be blank; allow save when loaded.
        return !isCreate;
    })();

    const productVariants = details?.affectedVariants ?? [];

    const save = async () => {
        if (!canSave) {
            setSaveError('Fill the required fields before saving');
            return;
        }
        try {
            setSaving(true);
            setSaveError(null);
            if (isCreate && level === 'brand') {
                const name = String(draft.name || '').trim();
                const body: Record<string, unknown> = {
                    level: 'brand',
                    name,
                    aliases: draft.aliases ?? [],
                };
                const countryOfOrigin = String(draft.countryOfOrigin || '').trim();
                const parentCompany = String(draft.parentCompany || '').trim();
                if (countryOfOrigin) body.countryOfOrigin = countryOfOrigin;
                if (parentCompany) body.parentCompany = parentCompany;
                await createMaster(body);
                toast.success('Brand created');
            } else if (isCreate && level === 'variant') {
                const alcoholProduct = String(draft.alcoholProduct || '');
                const name = String(draft.name || '').trim();
                const containerType = String(draft.containerType || '');
                const itemPerCase = Number(draft.itemPerCase);
                const volumeSettingId = String(draft.volumeSettingId || '');
                await createMaster({
                    level: 'variant',
                    name,
                    alcoholProduct,
                    containerType,
                    itemPerCase,
                    volumeSettingId,
                    aliases: draft.aliases ?? [],
                    gtin: draft.gtin ?? [],
                });
                toast.success('Variant created');
            } else if (id) {
                const body: Record<string, unknown> = { level, id };
                if (level === 'brand') {
                    body.name = String(draft.name || '').trim();
                    body.aliases = draft.aliases ?? [];
                    body.countryOfOrigin = draft.countryOfOrigin || undefined;
                    body.parentCompany = draft.parentCompany || undefined;
                } else if (level === 'product') {
                    body.name = String(draft.name || '').trim();
                    body.aliases = draft.aliases ?? [];
                    body.alcoholContent = Number(draft.alcoholContent);
                    body.description = draft.description || undefined;
                } else if (level === 'variant') {
                    body.name = String(draft.name || '').trim();
                    body.aliases = draft.aliases ?? [];
                    body.containerType = draft.containerType;
                    body.itemPerCase = Number(draft.itemPerCase);
                    body.gtin = draft.gtin ?? [];
                } else {
                    if (draft.retailPrice !== '' && draft.retailPrice != null)
                        body.retailPrice = Number(draft.retailPrice);
                    if (draft.purchasePrice !== '' && draft.purchasePrice != null)
                        body.purchasePrice = Number(draft.purchasePrice);
                    if (draft.wholesalePrice !== '' && draft.wholesalePrice != null)
                        body.wholesalePrice = Number(draft.wholesalePrice);
                    if (draft.distilleryPrice !== '' && draft.distilleryPrice != null)
                        body.distilleryPrice = Number(draft.distilleryPrice);
                    if (draft.itemPerCase !== '' && draft.itemPerCase != null)
                        body.itemPerCase = Number(draft.itemPerCase);
                    body.licenseType = draft.licenseType ?? [];
                    body.itemCode = draft.itemCode ?? [];
                    body.gtin = draft.gtin ?? [];
                    const existingCat =
                        details?.record?.exciseCategory != null
                            ? String(details.record.exciseCategory)
                            : '';
                    if (!existingCat) {
                        const assigned = String(draft.exciseCategory || '').trim();
                        if (assigned) body.exciseCategory = assigned;
                    }
                    // API allowlist key is exciseSubCategory (ObjectId); null clears it.
                    if (draft.exciseSubCategory !== undefined) {
                        const subId = String(draft.exciseSubCategory || '').trim();
                        body.exciseSubCategory = subId || null;
                    }
                }
                await updateMaster(body);
                toast.success('Saved');
            }
            onSaved();
            onClose();
        } catch (err) {
            setSaveError(getApiErrorMessage(err) || 'That did not save');
        } finally {
            setSaving(false);
        }
    };

    const volumeOptions = volumes.map((v) => ({
        value: v._id,
        label: `${v.value} ${volumeUnitShortLabel(v.unit)}`,
    }));

    const cancelVolAdd = () => {
        setVolAdding(false);
        setVolText('');
        setVolSaving(false);
        setVolError(null);
    };

    const commitStandardVolume = async () => {
        if (volSaving) return;
        const ml = parseInt(String(volText).replace(/[^0-9]/g, ''), 10);
        if (!ml || !Number.isFinite(ml) || ml <= 0) {
            setVolError('Enter the pack size in millilitres, for example 375.');
            return;
        }
        const existing = volumes.find(
            (v) => Number(v.value) === ml && v.unit === CPM_VOLUME_DEFAULT_UNIT
        );
        if (existing) {
            setField('volumeSettingId', existing._id);
            cancelVolAdd();
            toast.success(
                `${ml} ${volumeUnitShortLabel(CPM_VOLUME_DEFAULT_UNIT)} was already a standard volume — selected it here.`
            );
            return;
        }

        setVolSaving(true);
        setVolError(null);
        try {
            const data = await createVolumeSetting({
                value: ml,
                unit: CPM_VOLUME_DEFAULT_UNIT,
            });
            const created = data.setting;
            if (created?._id) {
                setVolumes((prev) =>
                    [...prev, created].sort((a, b) => Number(a.value) - Number(b.value))
                );
                setField('volumeSettingId', created._id);
            }
            cancelVolAdd();
            toast.success(
                `Added ${ml} ${volumeUnitShortLabel(CPM_VOLUME_DEFAULT_UNIT)} to the standard volume list in Volume Settings.`
            );
        } catch (err) {
            setVolSaving(false);
            setVolError(
                getApiErrorMessage(err) ||
                    'Volume Settings did not accept the write. Nothing was added — try again.'
            );
        }
    };

    const productOptions = products.map((p) => ({
        value: p._id,
        label: p.name,
    }));

    const productCount = details?.affectedProducts?.length ?? 0;
    const brandTenantLabel =
        details?.tenantLinkCount != null
            ? `${formatCpmCount(details.tenantLinkCount)} products`
            : '—';
    const brandDirtyLabel =
        brandDirtyKeys.length === 0
            ? 'No changes yet'
            : `${brandDirtyKeys.length} field${brandDirtyKeys.length === 1 ? '' : 's'} changed`;
    const productDirtyLabel =
        productDirtyKeys.length === 0
            ? 'No changes yet'
            : `${productDirtyKeys.length} field${productDirtyKeys.length === 1 ? '' : 's'} changed`;

    return (
        <CpmModal
            open={open}
            onClose={onClose}
            maxWidth={
                level === 'brand'
                    ? 920
                    : level === 'product'
                      ? 860
                      : level === 'stateProduct'
                        ? 720
                        : 640
            }
            zIndex={zIndex}
            closeOnEscape={isTop}
            closeOnBackdrop={isTop}
            showBackdrop={isTop}
        >
            <div
                className="flex items-start gap-[14px] border-b border-[var(--cpm-border-default)]"
                style={{ padding: '22px 24px 18px' }}
            >
                <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 7 }}>
                    <div className="flex items-center gap-[9px] flex-wrap">
                        <CpmTypeChip
                            recordType={
                                level === 'brand'
                                    ? 'BRAND'
                                    : level === 'product'
                                      ? 'PRODUCT'
                                      : level === 'variant'
                                        ? 'VARIANT'
                                        : 'PRODUCT'
                            }
                        />
                        {!isCreate && level !== 'brand' && level !== 'product' && status ? (
                            <CpmStatusChip status={status} />
                        ) : null}
                    </div>
                    <h2 className="m-0 !text-[21px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                        {isCreate
                            ? level === 'brand'
                                ? 'Create a brand'
                                : 'Create a variant'
                            : recordName}
                    </h2>
                    <span className="!text-[12.5px] !font-semibold !text-[var(--cpm-text-secondary)]">
                        {isCreate
                            ? level === 'brand'
                                ? 'Name the brand. Country, parent company and aliases are optional. The brand is created active as soon as you save.'
                                : 'Pick the product and a standard pack size. The variant is created active as soon as you save.'
                            : level === 'brand' || level === 'product'
                              ? 'Edit the fields below. Nothing is written until you save.'
                              : `Edit ${kindLabel.toLowerCase()} fields. Status is workflow-driven.`}
                    </span>
                    {tenantNote ? (
                        <span className="self-start mt-[2px] inline-flex items-center gap-[7px] px-[11px] py-[4px] rounded-full bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)] text-[11.5px] font-extrabold text-[var(--cpm-text-secondary)] tabular">
                            {level === 'product' ? (
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--cpm-brand-purple)"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="shrink-0"
                                >
                                    <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                                    <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
                                    <path d="M8 12h8" />
                                </svg>
                            ) : null}
                            {tenantNote}
                        </span>
                    ) : null}
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="shrink-0 inline-flex items-center justify-center w-[30px] h-[30px] rounded-[8px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                    >
                        <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                    </svg>
                </button>
            </div>

            <CpmModalBody
                className={
                    level === 'brand' || level === 'product'
                        ? '!px-6 !pt-4 !pb-[18px] !gap-0'
                        : undefined
                }
            >
                {loading ? (
                    <div className="flex flex-col" style={{ gap: 14 }} aria-busy="true">
                        <div className="flex items-center" style={{ gap: 10 }}>
                            <span className="cpm-spin" />
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}
                            >
                                Loading record
                            </span>
                        </div>
                        <span className="cpm-sk" style={{ height: 42, borderRadius: 10 }} />
                        <span className="cpm-sk" style={{ height: 42, borderRadius: 10 }} />
                        <span
                            className="cpm-sk"
                            style={{ height: 42, width: '70%', borderRadius: 10 }}
                        />
                    </div>
                ) : loadError ? (
                    <div
                        className="flex flex-col items-center text-center"
                        style={{ gap: 12, padding: '18px 8px 8px' }}
                    >
                        <span
                            className="inline-flex items-center justify-center bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                            style={{ width: 48, height: 48, borderRadius: 12 }}
                        >
                            <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                        </span>
                        <span
                            className="text-[var(--cpm-brand-navy)]"
                            style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.02em' }}
                        >
                            {loadMissing
                                ? "That record isn't in the master catalog"
                                : "Couldn't open this record"}
                        </span>
                        <span
                            className="text-[var(--cpm-text-secondary)] max-w-[340px]"
                            style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                        >
                            {loadMissing
                                ? 'It may have been deleted or the link is out of date. Nothing was changed.'
                                : loadError}
                        </span>
                        <div className="flex items-center" style={{ gap: 10, marginTop: 4 }}>
                            <CpmButton variant="outline" size="modal" onClick={onClose}>
                                Close
                            </CpmButton>
                            {!loadMissing ? (
                                <CpmButton
                                    variant="navy"
                                    size="modal"
                                    onClick={() => setLoadKey((k) => k + 1)}
                                >
                                    Try again
                                </CpmButton>
                            ) : null}
                        </div>
                    </div>
                ) : level === 'brand' ? (
                    <div
                        className="grid grid-cols-3 gap-x-[18px] gap-y-3"
                        style={{ alignContent: 'start' }}
                    >
                        <FormField
                            label="Name"
                            tag={brandDirtySet.has('name') ? 'Changed' : undefined}
                            tagChanged={brandDirtySet.has('name')}
                        >
                            <TextInput
                                value={String(draft.name ?? '')}
                                onChange={(v) => setField('name', v)}
                                placeholder="Brand name"
                                changed={brandDirtySet.has('name')}
                            />
                        </FormField>
                        <FormField
                            label="Aliases"
                            span2
                            tag={brandDirtySet.has('aliases') ? 'Changed' : undefined}
                            tagChanged={brandDirtySet.has('aliases')}
                        >
                            <ChipListEditor
                                values={(draft.aliases as string[]) ?? []}
                                onChange={(v) => setField('aliases', v)}
                                placeholder="Alias, then Enter"
                                addLabel="Add alias"
                                emptyLabel="No aliases yet"
                                changed={brandDirtySet.has('aliases')}
                            />
                        </FormField>
                        <FormField
                            label="Country of origin"
                            tag={brandDirtySet.has('countryOfOrigin') ? 'Changed' : undefined}
                            tagChanged={brandDirtySet.has('countryOfOrigin')}
                        >
                            <TextInput
                                value={String(draft.countryOfOrigin ?? '')}
                                onChange={(v) => setField('countryOfOrigin', v)}
                                changed={brandDirtySet.has('countryOfOrigin')}
                            />
                        </FormField>
                        <FormField
                            label="Parent company"
                            span2
                            tag={brandDirtySet.has('parentCompany') ? 'Changed' : undefined}
                            tagChanged={brandDirtySet.has('parentCompany')}
                        >
                            <TextInput
                                value={String(draft.parentCompany ?? '')}
                                onChange={(v) => setField('parentCompany', v)}
                                changed={brandDirtySet.has('parentCompany')}
                            />
                        </FormField>
                        <FormField
                            label="Status"
                            tag="Set by workflow"
                            help={
                                isCreate
                                    ? 'Records you create here are active as soon as you save.'
                                    : 'Set by the workflow that owns it — review, a merge, or a rejection.'
                            }
                        >
                            <ReadonlyField value={statusDisplayLabel(status)} />
                        </FormField>
                        {!isCreate ? (
                            <>
                                <FormField label="Products" tag="Joined field">
                                    <ReadonlyField
                                        value={`${formatCpmCount(productCount)} ${productCount === 1 ? 'product' : 'products'}`}
                                    />
                                </FormField>
                                <FormField
                                    label="Tenant links"
                                    tag="Tenant data"
                                    help="Store-brand records across live and trial tenants that roll up to this brand."
                                >
                                    <ReadonlyField value={brandTenantLabel} />
                                </FormField>
                            </>
                        ) : null}
                    </div>
                ) : level === 'product' ? (
                    <div
                        className="grid grid-cols-2 gap-x-[18px] gap-y-3"
                        style={{ alignContent: 'start' }}
                    >
                        <FormField
                            label="Name"
                            span2
                            tag={productDirtySet.has('name') ? 'Changed' : undefined}
                            tagChanged={productDirtySet.has('name')}
                        >
                            <TextInput
                                value={String(draft.name ?? '')}
                                onChange={(v) => setField('name', v)}
                                placeholder="Product name"
                                changed={productDirtySet.has('name')}
                            />
                        </FormField>
                        <FormField
                            label="Aliases"
                            span2
                            tag={productDirtySet.has('aliases') ? 'Changed' : undefined}
                            tagChanged={productDirtySet.has('aliases')}
                        >
                            <ChipListEditor
                                values={(draft.aliases as string[]) ?? []}
                                onChange={(v) => setField('aliases', v)}
                                placeholder="Alias, then Enter"
                                addLabel="Add alias"
                                emptyLabel="No aliases yet"
                                changed={productDirtySet.has('aliases')}
                            />
                        </FormField>
                        <FormField label="Brand" tag="Joined field">
                            <ReadonlyField
                                value={String(
                                    (details?.brand as { name?: string } | undefined)?.name ?? '—'
                                )}
                            />
                        </FormField>
                        <FormField
                            label="Alcohol content (%)"
                            tag={productDirtySet.has('alcoholContent') ? 'Changed' : undefined}
                            tagChanged={productDirtySet.has('alcoholContent')}
                        >
                            <TextInput
                                type="number"
                                value={String(draft.alcoholContent ?? '')}
                                onChange={(v) => setField('alcoholContent', v)}
                                changed={productDirtySet.has('alcoholContent')}
                            />
                        </FormField>
                        <FormField
                            label="Status"
                            span2
                            tag="Set by workflow"
                            help="Set by the workflow that owns it — review, a merge, or a rejection."
                        >
                            <ReadonlyField value={statusDisplayLabel(status)} />
                        </FormField>
                        <FormField
                            label="Description"
                            span2
                            tag={productDirtySet.has('description') ? 'Changed' : undefined}
                            tagChanged={productDirtySet.has('description')}
                        >
                            <TextInput
                                value={String(draft.description ?? '')}
                                onChange={(v) => setField('description', v)}
                                changed={productDirtySet.has('description')}
                            />
                        </FormField>
                        {!isCreate ? (
                            <FormField
                                label="Variants"
                                span2
                                tag="Read-only list"
                                help="Open a variant to edit it in its own record."
                            >
                                <div
                                    className="flex flex-col rounded-[10px] border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)] overflow-y-auto"
                                    style={{ gap: 6, padding: 6, maxHeight: 132 }}
                                >
                                    {productVariants.length === 0 ? (
                                        <span className="text-[12px] font-bold text-[var(--cpm-text-tertiary)] px-1 py-1.5">
                                            No variants under this product yet.
                                        </span>
                                    ) : (
                                        productVariants.map((cv) => {
                                            const containerType = String(
                                                (cv as { containerType?: string }).containerType ??
                                                    ''
                                            );
                                            const meta = [
                                                formatVolumeLabel(cv.volumeMl, cv.volumeUnit),
                                                containerType,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ');
                                            return (
                                                <div
                                                    key={cv._id}
                                                    className="flex items-center gap-[10px] px-[9px] py-[7px] rounded-[8px] bg-white border border-[var(--cpm-border-default)]"
                                                >
                                                    <span className="flex-1 min-w-0 text-[12.5px] font-extrabold leading-[1.3] text-[var(--cpm-text-primary)]">
                                                        {cv.name}
                                                    </span>
                                                    <span className="shrink-0 text-[11.5px] font-bold text-[var(--cpm-text-secondary)] tabular whitespace-nowrap">
                                                        {meta || '—'}
                                                    </span>
                                                    <CpmStatusChip status={cv.status} />
                                                    {onOpenVariant ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => onOpenVariant(cv._id)}
                                                            className="shrink-0 inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-full border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[11px] font-extrabold text-[var(--cpm-text-secondary)] whitespace-nowrap hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
                                                        >
                                                            Edit
                                                            <svg
                                                                width="11"
                                                                height="11"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2.6"
                                                            >
                                                                <path
                                                                    d="m9 18 6-6-6-6"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </button>
                                                    ) : null}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </FormField>
                        ) : null}
                    </div>
                ) : (
                    <div className="flex flex-col" style={{ gap: 16 }}>
                        {level === 'variant' ? (
                            <>
                                <div className="flex flex-col gap-[6px]">
                                    <FieldLabel label="Name" />
                                    <TextInput
                                        value={String(draft.name ?? '')}
                                        onChange={(v) => setField('name', v)}
                                        placeholder="Variant name"
                                    />
                                </div>
                                {isCreate ? (
                                    <div className="flex flex-col gap-[6px]">
                                        <FieldLabel label="Product" />
                                        {createProductId ? (
                                            <ReadonlyField
                                                value={createProductName ?? createProductId}
                                            />
                                        ) : (
                                            <CpmFilterDropdown
                                                label=""
                                                options={productOptions}
                                                selected={
                                                    draft.alcoholProduct
                                                        ? [String(draft.alcoholProduct)]
                                                        : []
                                                }
                                                multi={false}
                                                includeEmptyOption={false}
                                                emptyLabel="Select product"
                                                onChange={(vals) =>
                                                    setField('alcoholProduct', vals[0] ?? '')
                                                }
                                                className="!max-w-none !min-w-0 flex-none w-full"
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-[6px]">
                                        <FieldLabel label="Product · Brand" tag="Readonly" />
                                        <ReadonlyField
                                            value={
                                                [
                                                    (
                                                        details?.product as
                                                            | { name?: string }
                                                            | undefined
                                                    )?.name,
                                                    (
                                                        details?.brand as
                                                            | { name?: string }
                                                            | undefined
                                                    )?.name,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ') || '—'
                                            }
                                        />
                                    </div>
                                )}
                                {isCreate ? (
                                    <div className="flex flex-col gap-[6px]">
                                        <FieldLabel label="Volume" />
                                        <CpmFilterDropdown
                                            label=""
                                            options={volumeOptions}
                                            selected={
                                                draft.volumeSettingId
                                                    ? [String(draft.volumeSettingId)]
                                                    : []
                                            }
                                            multi={false}
                                            includeEmptyOption={false}
                                            emptyLabel="Select a standard volume"
                                            onChange={(vals) =>
                                                setField('volumeSettingId', vals[0] ?? '')
                                            }
                                            className="!max-w-none !min-w-0 flex-none w-full"
                                        />
                                        <div className="flex items-center flex-wrap gap-[10px]">
                                            {volAdding ? (
                                                <div className="flex items-center gap-[8px]">
                                                    <input
                                                        value={volText}
                                                        disabled={volSaving}
                                                        autoFocus
                                                        inputMode="numeric"
                                                        placeholder="Size in ML, then Enter"
                                                        onChange={(e) => {
                                                            setVolText(e.target.value);
                                                            setVolError(null);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                void commitStandardVolume();
                                                            } else if (e.key === 'Escape') {
                                                                e.preventDefault();
                                                                cancelVolAdd();
                                                            }
                                                        }}
                                                        className="rounded-[9px] border-[1.5px] border-[var(--cpm-brand-purple)] bg-white text-[var(--cpm-text-primary)] tabular-nums outline-none shadow-[0_0_0_3px_rgba(110,100,240,0.28)] disabled:opacity-60"
                                                        style={{
                                                            width: 190,
                                                            height: 34,
                                                            padding: '0 11px',
                                                            fontSize: 12.5,
                                                            fontWeight: 700,
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        disabled={volSaving}
                                                        onClick={() => void commitStandardVolume()}
                                                        className="inline-flex items-center gap-[6px] rounded-[9px] bg-[var(--cpm-brand-purple)] text-white cursor-pointer hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed"
                                                        style={{
                                                            height: 34,
                                                            padding: '0 13px',
                                                            fontSize: 12,
                                                            fontWeight: 800,
                                                        }}
                                                    >
                                                        {volSaving ? (
                                                            <span
                                                                className="cpm-spin-w"
                                                                style={{
                                                                    width: 12,
                                                                    height: 12,
                                                                    borderWidth: 2,
                                                                }}
                                                            />
                                                        ) : (
                                                            <svg
                                                                width="12"
                                                                height="12"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="3"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            >
                                                                <path d="M20 6 9 17l-5-5" />
                                                            </svg>
                                                        )}
                                                        {volSaving ? 'Adding…' : 'Add to list'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={volSaving}
                                                        onClick={cancelVolAdd}
                                                        className="bg-transparent border-0 p-0 cursor-pointer text-[var(--cpm-text-tertiary)] hover:text-[var(--cpm-brand-navy)] disabled:opacity-50"
                                                        style={{ fontSize: 12, fontWeight: 800 }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setVolAdding(true);
                                                        setVolText('');
                                                        setVolError(null);
                                                    }}
                                                    className="inline-flex items-center gap-[6px] rounded-[9px] border-[1.5px] border-dashed border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)] cursor-pointer hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
                                                    style={{
                                                        height: 34,
                                                        padding: '0 13px',
                                                        fontSize: 12,
                                                        fontWeight: 800,
                                                    }}
                                                >
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="3"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <path d="M5 12h14" />
                                                        <path d="M12 5v14" />
                                                    </svg>
                                                    Add a standard volume
                                                </button>
                                            )}
                                            <Link
                                                to="/central-product-master/volume-settings"
                                                className="inline-flex items-center gap-[6px] self-center !text-[12px] !font-extrabold !text-[var(--cpm-brand-purple)] border-b border-[var(--cpm-brand-purple)]"
                                            >
                                                Open Volume settings
                                            </Link>
                                            {volError ? (
                                                <span
                                                    className="basis-full flex items-center gap-[7px] text-[var(--cpm-shortfall-text)]"
                                                    style={{
                                                        fontSize: 11.5,
                                                        fontWeight: 700,
                                                        lineHeight: 1.45,
                                                    }}
                                                >
                                                    <svg
                                                        width="13"
                                                        height="13"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2.4"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        className="shrink-0"
                                                    >
                                                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                                        <path d="M12 9v4" />
                                                        <path d="M12 17h.01" />
                                                    </svg>
                                                    {volError}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-[6px]">
                                        <FieldLabel label="Volume" tag="Immutable" />
                                        <ReadonlyField
                                            value={formatVolumeLabel(
                                                details?.record?.volumeMl as number | undefined,
                                                details?.record?.volumeUnit as string | undefined
                                            )}
                                        />
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-[14px]">
                                    <div className="flex flex-col gap-[6px]">
                                        <FieldLabel label="Container" />
                                        <CpmFilterDropdown
                                            label=""
                                            options={[...CPM_CONTAINER_TYPE_OPTIONS]}
                                            selected={
                                                draft.containerType
                                                    ? [String(draft.containerType)]
                                                    : []
                                            }
                                            multi={false}
                                            includeEmptyOption={false}
                                            emptyLabel="Select"
                                            onChange={(vals) =>
                                                setField('containerType', vals[0] ?? '')
                                            }
                                            className="!max-w-none !min-w-0 flex-none w-full"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-[6px]">
                                        <FieldLabel label="Items / case" />
                                        <TextInput
                                            type="number"
                                            value={String(draft.itemPerCase ?? '')}
                                            onChange={(v) => setField('itemPerCase', v)}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-[6px]">
                                    <FieldLabel label="Aliases" />
                                    <ChipListEditor
                                        values={(draft.aliases as string[]) ?? []}
                                        onChange={(v) => setField('aliases', v)}
                                        placeholder="Alias, then Enter"
                                        addLabel="Add"
                                    />
                                </div>
                                <div className="flex flex-col gap-[6px]">
                                    <FieldLabel label="Barcodes / GTINs" />
                                    <ChipListEditor
                                        values={(draft.gtin as string[]) ?? []}
                                        onChange={(v) => setField('gtin', v)}
                                        placeholder="GTIN, then Enter"
                                        addLabel="Add"
                                    />
                                </div>
                            </>
                        ) : null}

                        {level === 'stateProduct' ? (
                            <>
                                <div className="grid grid-cols-2 gap-[14px]">
                                    <div className="flex flex-col gap-[6px]">
                                        <FieldLabel label="State" tag="Readonly" />
                                        <ReadonlyField
                                            value={String(details?.record?.state ?? '—')}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-[6px]">
                                        <FieldLabel label="Variant" tag="Readonly" />
                                        <ReadonlyField
                                            value={String(
                                                (details?.variant as { name?: string } | undefined)
                                                    ?.name ?? '—'
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-[6px]">
                                    {(() => {
                                        const existingCatId =
                                            details?.record?.exciseCategory != null
                                                ? String(details.record.exciseCategory)
                                                : '';
                                        const existingCatName = String(
                                            details?.record?.exciseCategoryName ?? ''
                                        );
                                        const categoryLocked = !!(existingCatId || existingCatName);
                                        const recState = String(details?.record?.state ?? '');
                                        const assignOptions = exciseCategories
                                            .slice()
                                            .sort((a, b) =>
                                                a.category.localeCompare(b.category, undefined, {
                                                    sensitivity: 'base',
                                                })
                                            )
                                            .map((c) => ({
                                                value: String(c._id),
                                                label: c.category,
                                            }));
                                        const draftCatId = String(draft.exciseCategory || '');
                                        const assignDirty = !categoryLocked && !!draftCatId;
                                        if (categoryLocked) {
                                            return (
                                                <>
                                                    <FieldLabel
                                                        label="Excise category"
                                                        tag={CPM_LICENCE_EXCISE_COPY.TAG_SET_ONCE}
                                                    />
                                                    <ReadonlyField
                                                        value={existingCatName || existingCatId}
                                                    />
                                                    <span className="text-[11.5px] font-semibold leading-[1.45] text-[var(--cpm-text-secondary)] text-pretty">
                                                        {CPM_LICENCE_EXCISE_COPY.LOCKED_HELP}
                                                    </span>
                                                </>
                                            );
                                        }
                                        return (
                                            <>
                                                <FieldLabel
                                                    label={CPM_LICENCE_EXCISE_COPY.ASSIGN_LABEL}
                                                    tag={
                                                        assignDirty
                                                            ? 'Changed'
                                                            : CPM_LICENCE_EXCISE_COPY.TAG_REQUIRED_FIELD
                                                    }
                                                    tagChanged={assignDirty}
                                                />
                                                <CpmFilterDropdown
                                                    label=""
                                                    options={assignOptions}
                                                    selected={draftCatId ? [draftCatId] : []}
                                                    multi={false}
                                                    includeEmptyOption={false}
                                                    emptyLabel={cpmLicenceExcisePickForState(
                                                        recState || 'this state'
                                                    )}
                                                    highlightSelected={assignDirty}
                                                    portal
                                                    triggerHeight={42}
                                                    onChange={(vals) => {
                                                        const next = vals[0] ?? '';
                                                        setDraft((prev) => ({
                                                            ...prev,
                                                            exciseCategory: next,
                                                            exciseSubCategory: '',
                                                        }));
                                                    }}
                                                    className="!max-w-none !min-w-0 flex-none w-full"
                                                />
                                                <span className="text-[11.5px] font-semibold leading-[1.45] text-[var(--cpm-text-secondary)] text-pretty">
                                                    {CPM_LICENCE_EXCISE_COPY.ASSIGN_HELP}
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>
                                <div className="flex flex-col gap-[6px]">
                                    <FieldLabel label="Excise subcategory" />
                                    {(() => {
                                        const existingCatId =
                                            details?.record?.exciseCategory != null
                                                ? String(details.record.exciseCategory)
                                                : '';
                                        const catId =
                                            existingCatId || String(draft.exciseCategory || '');
                                        const cat =
                                            exciseCategories.find((c) => String(c._id) === catId) ??
                                            null;
                                        const subOptions = (cat?.subCategories ?? []).map((s) => ({
                                            value: String(s._id),
                                            label: s.name,
                                        }));
                                        if (!catId) {
                                            return (
                                                <ReadonlyField value="Map an excise category first" />
                                            );
                                        }
                                        if (subOptions.length === 0) {
                                            return (
                                                <ReadonlyField value="No subcategories curated for this category" />
                                            );
                                        }
                                        return (
                                            <CpmFilterDropdown
                                                label=""
                                                options={subOptions}
                                                selected={
                                                    draft.exciseSubCategory
                                                        ? [String(draft.exciseSubCategory)]
                                                        : []
                                                }
                                                multi={false}
                                                emptyLabel="None"
                                                portal
                                                triggerHeight={42}
                                                onChange={(vals) =>
                                                    setField('exciseSubCategory', vals[0] ?? '')
                                                }
                                                className="!max-w-none !min-w-0 flex-none w-full"
                                            />
                                        );
                                    })()}
                                    <span className="text-[11.5px] font-semibold text-[var(--cpm-text-secondary)]">
                                        Sends the curated subcategory id — leave empty to clear.
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-[14px]">
                                    {(
                                        [
                                            ['retailPrice', 'Retail price'],
                                            ['purchasePrice', 'Purchase price'],
                                            ['wholesalePrice', 'Wholesale price'],
                                            ['distilleryPrice', 'Distillery price'],
                                            ['itemPerCase', 'Items / case'],
                                        ] as const
                                    ).map(([key, label]) => (
                                        <div key={key} className="flex flex-col gap-[6px]">
                                            <FieldLabel label={label} />
                                            <TextInput
                                                type="number"
                                                value={String(draft[key] ?? '')}
                                                onChange={(v) => setField(key, v)}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="flex flex-col gap-[6px]">
                                    <FieldLabel label="License type" />
                                    {(() => {
                                        const recState = String(details?.record?.state ?? '');
                                        const curated = recState
                                            ? resolvedLicenseMap[recState]
                                            : undefined;
                                        const selected = (draft.licenseType as string[]) ?? [];
                                        const emptyCurated =
                                            resolvedLicenseReady &&
                                            (curated == null || curated.length === 0);
                                        const disabled = !resolvedLicenseReady || emptyCurated;
                                        return (
                                            <>
                                                <CpmFilterDropdown
                                                    label=""
                                                    options={cpmLicenseTypeOptions(
                                                        curated,
                                                        selected
                                                    )}
                                                    selected={selected}
                                                    multi
                                                    emptyLabel="None"
                                                    disabled={disabled}
                                                    onChange={(vals) =>
                                                        setField('licenseType', vals)
                                                    }
                                                    className="!max-w-none !min-w-0 flex-none w-full"
                                                />
                                                {emptyCurated ? (
                                                    <span className="text-[11.5px] font-semibold text-[var(--cpm-text-secondary)]">
                                                        {CPM_LICENSE_TYPES_COPY.MASTER_EMPTY_HELPER}
                                                    </span>
                                                ) : null}
                                            </>
                                        );
                                    })()}
                                </div>
                                <div className="flex flex-col gap-[6px]">
                                    <FieldLabel label="Item codes" />
                                    <ChipListEditor
                                        values={(draft.itemCode as string[]) ?? []}
                                        onChange={(v) => setField('itemCode', v)}
                                        placeholder="Code, then Enter"
                                        addLabel="Add"
                                    />
                                </div>
                            </>
                        ) : null}
                    </div>
                )}
            </CpmModalBody>

            <CpmModalFooter className="!items-center gap-[14px] flex-wrap">
                {level === 'brand' && !isCreate ? (
                    <span
                        className="text-[11.5px] font-extrabold"
                        style={{
                            color:
                                brandDirtyKeys.length === 0
                                    ? 'var(--cpm-text-tertiary)'
                                    : 'var(--cpm-brand-purple)',
                        }}
                    >
                        {brandDirtyLabel}
                    </span>
                ) : null}
                {level === 'product' && !isCreate ? (
                    <span
                        className="text-[11.5px] font-extrabold"
                        style={{
                            color:
                                productDirtyKeys.length === 0
                                    ? 'var(--cpm-text-tertiary)'
                                    : 'var(--cpm-brand-purple)',
                        }}
                    >
                        {productDirtyLabel}
                    </span>
                ) : null}
                {!isCreate && level === 'brand' && id && onRequestDelete ? (
                    <button
                        type="button"
                        onClick={() => onRequestDelete('brand', id, recordName)}
                        className="inline-flex items-center gap-[5px] text-[11.5px] font-extrabold text-[var(--cpm-text-tertiary)] border-b border-[var(--cpm-border-strong)] hover:text-[var(--cpm-shortfall-text)] hover:border-[var(--cpm-shortfall-text)]"
                    >
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                        >
                            <path d="M3 6h18" strokeLinecap="round" />
                            <path
                                d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path d="M10 11v6M14 11v6" strokeLinecap="round" />
                        </svg>
                        Delete brand
                    </button>
                ) : null}
                {saveError ? (
                    <div
                        className="flex items-start rounded-[10px] bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)]"
                        style={{
                            gap: 10,
                            padding: '10px 12px',
                            marginRight: 'auto',
                            maxWidth: 360,
                        }}
                    >
                        <span
                            className="text-[var(--cpm-shortfall-text)]"
                            style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.4 }}
                        >
                            {saveError}
                        </span>
                    </div>
                ) : null}
                <div className="ml-auto flex items-center gap-[10px]">
                    <CpmButton variant="outline" size="modal" onClick={onClose} disabled={saving}>
                        Cancel
                    </CpmButton>
                    <CpmButton
                        variant="navy"
                        size="modal"
                        onClick={save}
                        isLoading={saving || loading}
                        disabled={!canSave || saving || loading || !!loadError}
                        leadingIcon={
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                            >
                                <path
                                    d="M20 6 9 17l-5-5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        }
                    >
                        {saving
                            ? isCreate
                                ? 'Creating…'
                                : 'Saving…'
                            : isCreate
                              ? level === 'brand'
                                  ? 'Create brand'
                                  : 'Create variant'
                              : saveError
                                ? 'Try again'
                                : 'Save changes'}
                    </CpmButton>
                </div>
            </CpmModalFooter>
        </CpmModal>
    );
}
